/**
 * O variograma da análise espacial CPTu (onda 316, R4): uma linha por coluna interpolada — «pepita · patamar
 * · alcance», os parâmetros («8 classes até 63,25 m · cotas a cada 0,25 m») e o motivo ao lado quando ele vem
 * («o alcance ficou no limite da busca (120,00 m): os dados não o definem») —, e o gráfico da coluna
 * escolhida: os pontos experimentais (com os pares escritos) e a curva ajustada. Com o IDW, «IDW, potência 2»
 * e o variograma «não usado pelo IDW»; quando a krigagem não se ajustou, a frase do método — nunca calado.
 * O nome é `GraficoDoVariograma` porque `Variograma` é o tipo do motor.
 */
import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { gamaDoModelo } from '../motor';
import { escala, marcas, numeroBr } from './escalas';
import type { VariogramaDaColuna } from './espacialDaCampanha';
import { useLargura } from './useLargura';

const ESQUERDA = 52;
const BAIXO = 28;
const TOPO = 12;
const DIREITA = 12;
const ALTURA = 200;

/** O rótulo de uma coluna interpolada. */
const ROTULO_DA_COLUNA: Record<string, string> = {
  logFr: 'log₁₀ Fr', logQtn: 'log₁₀ Qtn', ic: 'Ic', qc: 'qc', qt: 'qt', fs: 'fs', u2: 'u₂', su: 'Su',
};
export const rotuloDaColuna = (c: string): string => ROTULO_DA_COLUNA[c] ?? c;

const NOME_DO_MODELO: Record<string, string> = { esferico: 'esférico', exponencial: 'exponencial' };

/** «pepita 0,152 · patamar 0,265 · alcance 120,00 m», ou «sem ajuste». */
export function textoDoAjuste(v: VariogramaDaColuna): string {
  const a = v.ajustado;
  if (!a.ajustado) return 'sem ajuste';
  return `pepita ${numeroBr(a.pepita, 3)} · patamar ${numeroBr(a.patamar, 3)} · alcance ${numeroBr(a.alcance, 2)} m`;
}

/** «8 classes até 63,25 m · cotas a cada 0,25 m». */
export function textoDosParametros(v: VariogramaDaColuna): string {
  return `${v.parametros.classes} classes até ${numeroBr(v.parametros.hMax, 2)} m · cotas a cada ${numeroBr(v.parametros.passoDasCotas, 2)} m`;
}

export function GraficoDoVariograma(p: {
  variogramas: readonly VariogramaDaColuna[];
  metodoUsado: 'idw' | 'krigagem';
  motivoDoMetodo: string | null;
}): JSX.Element {
  const [caixa, largura] = useLargura<HTMLDivElement>();
  const [escolhida, setEscolhida] = useState<string | null>(null);
  const atual = p.variogramas.find((v) => v.coluna === escolhida) ?? p.variogramas[0] ?? null;

  const desenho = useMemo(() => {
    if (!atual || largura <= 0) return null;
    const pontos = atual.experimental.filter((c) => c.pares > 0 && Number.isFinite(c.gama));
    const hMax = Math.max(atual.parametros.hMax, ...pontos.map((c) => c.h), 1);
    const gMax = Math.max(...pontos.map((c) => c.gama), atual.ajustado.ajustado ? atual.ajustado.patamar : 0, 1e-9);
    const ex = escala('linear', [0, hMax], [ESQUERDA, Math.max(ESQUERDA + 1, largura - DIREITA)]);
    const ey = escala('linear', [0, gMax * 1.1], [TOPO + ALTURA - BAIXO, TOPO]);
    let curva = '';
    if (atual.ajustado.ajustado) {
      const n = 80;
      for (let i = 0; i <= n; i++) {
        const h = (hMax * i) / n;
        const g = gamaDoModelo(atual.ajustado, h);
        if (!Number.isFinite(g)) continue;
        curva += `${curva ? 'L' : 'M'}${ex.para(h).toFixed(1)},${ey.para(g).toFixed(1)}`;
      }
    }
    return { pontos, ex, ey, hMax, gMax, curva, marcasX: marcas('linear', [0, hMax], 5), marcasY: marcas('linear', [0, gMax * 1.1], 4) };
  }, [atual, largura]);

  return (
    <div ref={caixa} className="cptu-variograma" role="group" aria-label="Variograma">
      {p.metodoUsado === 'idw' ? (
        <p className="cptu-variograma-metodo">
          <b>IDW, potência 2</b>{p.motivoDoMetodo ? ` — ${p.motivoDoMetodo}` : ' — variograma não usado pelo IDW'}.
        </p>
      ) : (
        <p className="cptu-variograma-metodo"><b>Krigagem ordinária</b>{atual ? ` · modelo ${NOME_DO_MODELO[atual.ajustado.modelo] ?? atual.ajustado.modelo}` : ''}.</p>
      )}
      <ul className="cptu-variograma-lista">
        {p.variogramas.map((v) => (
          <li key={v.coluna} className={atual && atual.coluna === v.coluna ? 'sel' : undefined} data-coluna={v.coluna}>
            <button type="button" className="cptu-variograma-escolha" aria-pressed={atual?.coluna === v.coluna} onClick={() => setEscolhida(v.coluna)}>
              {rotuloDaColuna(v.coluna)}
            </button>
            {' '}
            <span className="cptu-variograma-ajuste">{textoDoAjuste(v)}</span>
            {' · '}
            <span className="cptu-variograma-parametros">{textoDosParametros(v)}</span>
            {v.ajustado.motivo ? <span className="cptu-variograma-motivo"> — {v.ajustado.motivo}</span> : null}
            {p.metodoUsado === 'idw' ? <span className="cptu-nota"> (não usado pelo IDW)</span> : null}
          </li>
        ))}
      </ul>
      {atual && desenho && (
        <svg className="cptu-variograma-svg" width={largura} height={TOPO + ALTURA} role="img"
          aria-label={`Variograma de ${rotuloDaColuna(atual.coluna)}: ${textoDoAjuste(atual)}; ${desenho.pontos.length} classes com pares`}>
          <rect className="cptu-moldura" x={ESQUERDA} y={TOPO} width={Math.max(1, largura - DIREITA - ESQUERDA)} height={ALTURA - BAIXO} />
          {desenho.marcasY.map((g) => (
            <g key={`g${g}`}>
              <line className="cptu-grade" x1={ESQUERDA} x2={largura - DIREITA} y1={desenho.ey.para(g)} y2={desenho.ey.para(g)} />
              <text className="cptu-texto cptu-texto-2" x={ESQUERDA - 6} y={desenho.ey.para(g) + 4} textAnchor="end">{numeroBr(g, 3)}</text>
            </g>
          ))}
          {desenho.marcasX.map((h) => (
            <g key={`h${h}`}>
              <line className="cptu-grade" x1={desenho.ex.para(h)} x2={desenho.ex.para(h)} y1={TOPO} y2={TOPO + ALTURA - BAIXO} />
              <text className="cptu-texto cptu-texto-2" x={desenho.ex.para(h)} y={TOPO + ALTURA - BAIXO + 14} textAnchor="middle">{numeroBr(h, 0)}</text>
            </g>
          ))}
          <text className="cptu-texto cptu-texto-2" x={largura - DIREITA} y={TOPO + ALTURA - 2} textAnchor="end">h (m)</text>
          <text className="cptu-texto cptu-texto-2" x={ESQUERDA - 6} y={TOPO + 4} textAnchor="end">γ</text>
          {desenho.curva && <path className="cptu-variograma-curva" d={desenho.curva} />}
          {desenho.pontos.map((c) => (
            <g key={c.h} className="cptu-variograma-ponto">
              <circle cx={desenho.ex.para(c.h)} cy={desenho.ey.para(c.gama)} r={3 + Math.min(4, Math.log10(Math.max(1, c.pares)))} />
              <text className="cptu-texto cptu-texto-2" x={desenho.ex.para(c.h)} y={desenho.ey.para(c.gama) - 9} textAnchor="middle">{c.pares} pares</text>
            </g>
          ))}
        </svg>
      )}
    </div>
  );
}
